import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  message,
  Spin,
  Divider,
  Tag,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { DatePicker } from "antd";

import {
  getPotonganList,
  createPotongan,
  updatePotongan,
  deletePotongan,
} from "../../api/transaksi/Potongan";

import { dropdownKaryawan } from "../../api/master/Karyawan";

const { Option } = Select;

const PotonganGaji = () => {
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  const [data, setData] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingData, setEditingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [karyawanList, setKaryawanList] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getPotonganList();
      if (response.status) {
        setData(response.data);
      }
    } catch (error) {
      message.error("Gagal mengambil data potongan");
    } finally {
      setLoading(false);
    }
  };
  const fetchKaryawan = async () => {
    try {
      const result = await dropdownKaryawan();
      setKaryawanList(result.data);
    } catch (error) {
      message.error("Gagal memuat data karyawan.");
    }
  };
  useEffect(() => {
    fetchData();
    fetchKaryawan();
  }, []);

  const getColumnMonthFilterProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <DatePicker
          picker="month"
          value={selectedKeys[0] ? dayjs(selectedKeys[0], "YYYY-MM") : null}
          onChange={(date, dateString) => {
            setSelectedKeys(dateString ? [dateString] : []);
          }}
          style={{ width: "100%", marginBottom: 8 }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: "100%", marginBottom: 8 }}
        >
          Cari
        </Button>
        <Button
          onClick={() => {
            clearFilters();
            confirm({ closeDropdown: true });
          }}
          size="small"
          style={{ width: "100%" }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => record[dataIndex]?.startsWith(value),
    render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
  });

  const handleAdd = () => {
    form.resetFields();
    setEditingData(null);
    setFormVisible(true);
  };

  const handleEdit = (record) => {
    setEditingData(record);
    form.setFieldsValue({
      ...record,
      ptg_periode: dayjs(record.ptg_periode),
    });
    setFormVisible(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await deletePotongan(id);
      message.success("Data berhasil dihapus");
      fetchData();
    } catch (err) {
      message.error("Gagal menghapus data");
    }
  };

  const handleFinish = async (values) => {
    setSubmitLoading(true);
    const formatted = {
      ...values,
      ptg_periode: values.ptg_periode.format("YYYY-MM"),
    };

    try {
      if (editingData) {
        await updatePotongan(editingData.ptg_id, {
          ptg_jumlah: formatted.ptg_jumlah,
          ptg_keterangan: formatted.ptg_keterangan,
          ptg_status: formatted.ptg_status,
          ptg_updated_by: sessiData[0].user[0].fullname,
        });
        message.success("Data berhasil diperbarui");
      } else {
        await createPotongan({
          ...formatted,
          ptg_created_by: sessiData[0].user[0].fullname,
        });
        message.success("Data berhasil ditambahkan");
      }
      fetchData();
      setFormVisible(false);
    } catch (error) {
      message.error("Gagal menyimpan data");
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = [
    {
      title: "Nama Karyawan",
      dataIndex: "emp_nama",
      key: "emp_nama",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Cari nama"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={confirm}
              size="small"
            >
              Cari
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.emp_nama?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Periode",
      dataIndex: "ptg_periode",
      key: "ptg_periode",
      sorter: (a, b) => a.ptg_periode.localeCompare(b.ptg_periode),
      ...getColumnMonthFilterProps("ptg_periode"),
      render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
    },
    {
      title: "Jumlah",
      dataIndex: "ptg_jumlah",
      key: "ptg_jumlah",
      render: (val) => `${Number(val).toLocaleString("id-ID")}`,
      sorter: (a, b) => Number(a.ptg_jumlah) - Number(b.ptg_jumlah),
    },
    {
      title: "Keterangan",
      dataIndex: "ptg_keterangan",
      key: "ptg_keterangan",
    },
    {
      title: "Status",
      dataIndex: "ptg_status",
      editable: true,
      render: (val) => (
        <Tag color={val === "Aktif" ? "green" : "red"}>{val}</Tag>
      ),
      filters: [
        { text: "Aktif", value: "Aktif" },
        { text: "Tidak Aktif", value: "Tidak Aktif" },
      ],
      onFilter: (value, record) => record.ptg_status === value,
    },
    {
      title: "Aksi",
      key: "aksi",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleEdit(record)}
            icon={<EditOutlined />}
          >
            Edit
          </Button>
          <Popconfirm
            title="Yakin hapus data ini?"
            onConfirm={() => handleDelete(record.ptg_id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Divider>Input Potongan Gaji</Divider>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ marginBottom: 16 }}
      >
        Tambah Potongan Gaji
      </Button>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            pageSizeOptions: ["10", "20", "40", "60"],
            showSizeChanger: true,
            defaultPageSize: 20,
          }}
          rowKey="ptg_id"
        />
      </Spin>

      <Modal
        open={formVisible}
        title={editingData ? "Edit Potongan Gaji" : "Tambah Potongan Gaji"}
        onCancel={() => setFormVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="ptg_karyawan_id"
            label="Nama Karyawan"
            rules={[{ required: true, message: "Harap pilih karyawan" }]}
          >
            <Select placeholder="Pilih karyawan">
              {karyawanList.map((k) => (
                <Option key={k.emp_id_karyawan} value={k.emp_id_karyawan}>
                  {k.emp_nama}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ptg_periode"
            label="Periode"
            rules={[{ required: true, message: "Pilih periode" }]}
          >
            <DatePicker picker="month" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="ptg_jumlah"
            label="Jumlah Potongan"
            rules={[{ required: true, message: "Masukkan jumlah" }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              formatter={(value) =>
                value ? `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => value.replace(/Rp\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            name="ptg_keterangan"
            label="Keterangan"
            rules={[{ required: true, message: "Keterangan wajib diisi" }]}
          >
            <Input.TextArea rows={3} placeholder="Alasan potongan gaji" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="ptg_status"
            rules={[{ required: true, message: "Status wajib dipilih" }]}
          >
            <Select placeholder="Pilih status">
              <Option value="Aktif">Aktif</Option>
              <Option value="Tidak Aktif">Tidak Aktif</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PotonganGaji;
